import React, { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AppState, Specialty } from './types';
import { ProgressBar } from './components/ProgressBar';
import { HookScreen } from './components/steps/HookScreen';
import { QuizScreen } from './components/steps/QuizScreen';
import { PerceptionCapture } from './components/steps/PerceptionCapture';
import { SpecialtySelection } from './components/steps/SpecialtySelection';
import { SpecialtyDeepDive } from './components/steps/SpecialtyDeepDive';
import { TreatmentBehavior } from './components/steps/TreatmentBehavior';
import { DriversBarriers } from './components/steps/DriversBarriers';
import { RealityCheck } from './components/steps/RealityCheck';
import { PerceptionReveal } from './components/steps/PerceptionReveal';
import { CrossSpecialtyComparison } from './components/steps/CrossSpecialtyComparison';
import { SystemGap } from './components/steps/SystemGap';
import { BrandContext } from './components/steps/BrandContext';
import { Activation } from './components/steps/Activation';
import { Summary } from './components/steps/Summary';
const TOTAL_STEPS = 14;
const initialState: AppState = {
  quizAnswer: null,
  selectedSpecialty: null,
  predictedBehavior: null,
  predictedDriver: null,
  predictedBarrier: null,
  perceptionMode: false,
  perceptionNeph: null,
  perceptionCardio: null,
  perceptionEndo: null,
  perceptionPCP: null
};
export function App() {
  const [step, setStep] = useState(1);
  const [state, setState] = useState<AppState>(initialState);
  const [direction, setDirection] = useState(1);
  const updateState = useCallback((updates: Partial<AppState>) => {
    setState(prev => ({
      ...prev,
      ...updates
    }));
  }, []);
  const nextStep = useCallback(() => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, TOTAL_STEPS));
  }, []);
  const prevStep = useCallback(() => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  }, []);
  const goToStep = useCallback((target: number) => {
    setDirection(target > step ? 1 : -1);
    setStep(Math.max(1, Math.min(target, TOTAL_STEPS)));
  }, [step]);
  const resetApp = useCallback(() => {
    setState(initialState);
    setDirection(-1);
    setStep(1);
  }, []);
  const stepProps = {
    state,
    updateState,
    nextStep,
    prevStep,
    goToStep,
    resetApp
  };
  const renderStep = () => {
    switch (step) {
      case 1:
        return <HookScreen {...stepProps} data-id="element-0" />;
      case 2:
        return <QuizScreen {...stepProps} data-id="element-1" />;
      case 3:
        return <PerceptionCapture {...stepProps} data-id="element-2" />;
      case 4:
        return <SpecialtySelection {...stepProps} data-id="element-3" />;
      case 5:
        return <SpecialtyDeepDive {...stepProps} data-id="element-4" />;
      case 6:
        return <TreatmentBehavior {...stepProps} data-id="element-5" />;
      case 7:
        return <DriversBarriers {...stepProps} data-id="element-6" />;
      case 8:
        return <RealityCheck {...stepProps} data-id="element-7" />;
      case 9:
        return <PerceptionReveal {...stepProps} data-id="element-8" />;
      case 10:
        return <CrossSpecialtyComparison {...stepProps} data-id="element-9" />;
      case 11:
        return <SystemGap {...stepProps} data-id="element-10" />;
      case 12:
        return <BrandContext {...stepProps} data-id="element-11" />;
      case 13:
        return <Activation {...stepProps} data-id="element-12" />;
      case 14:
        return <Summary {...stepProps} data-id="element-13" />;
      default:
        return <HookScreen {...stepProps} data-id="element-14" />;
    }
  };
  return <div className="min-h-screen bg-white text-gray-900 font-sans" data-id="element-15">
      {step > 1 && <ProgressBar currentStep={step} totalSteps={TOTAL_STEPS} data-id="element-16" />}
      <AnimatePresence mode="wait" data-id="element-17">
        <motion.div key={step} initial={{
        opacity: 0,
        y: direction * 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: direction * -20
      }} transition={{
        duration: 0.35,
        ease: 'easeInOut'
      }} data-id="element-18">
          {renderStep()}
        </motion.div>
      </AnimatePresence>
    </div>;
}